/**
 * Leads tab - Companies + Jobs view.
 *
 *   Left:  Company list with filters + bulk add + CSV upload
 *   Right: Selected company detail with open jobs + contacts
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, Upload, Plus, Search, RefreshCw, Wand2, Building2, Briefcase,
  Mail, ExternalLink, ChevronRight, X, UserPlus, Linkedin, Zap, Globe, FileText,
} from 'lucide-react';

import type { ApiCall } from '../../api/interview';
import {
  type Company, type Job, type Contact,
  type DecisionMakerSearch, type EmailPattern,
  type DecisionMakerDiscovery, type DecisionMakerCandidate,
  listCompanies, getCompany, listJobsByCompany, listContacts,
  bulkAddCompanies, uploadCompaniesCSV, detectAts, updateCompany,
  getDecisionMakerSearch, getEmailPattern, createContact,
  discoverDecisionMakers,
} from '../../api/b2b';
import { Centered, ErrorBox, LoadingNudge, useDebouncedValue, useStepProgress, HeadingWithInfo, InfoTip, ActionWithInfo } from './_ui';
import B2BDiscoveryPanel from './B2BDiscoveryPanel';
import B2BWorkersPanel from './B2BWorkersPanel';

interface Props { apiCall: ApiCall; }

const TIER_LABELS: Record<string, string> = {
  cold: 'Cold', warm: 'Warm', hot: 'Hot', customer: 'Customer', lost: 'Lost', blocked: 'Blocked',
};

const LIST_PAGE_SIZE = 100;

const SOURCE_STYLES: Record<string, { label: string; bg: string; fg: string }> = {
  bundesagentur: { label: 'BA',  bg: 'bg-pink-100',    fg: 'text-pink-700' },
  csv_upload:    { label: 'CSV', bg: 'bg-amber-100',   fg: 'text-amber-700' },
  manual_bulk:   { label: 'Bulk',bg: 'bg-blue-100',    fg: 'text-blue-700' },
  manual:        { label: 'Manual', bg: 'bg-slate-100', fg: 'text-slate-700' },
  ats_slug:      { label: 'ATS', bg: 'bg-indigo-100',  fg: 'text-indigo-700' },
};

const SourceBadge: React.FC<{ source: string }> = ({ source }) => {
  const style = SOURCE_STYLES[source] || { label: source.slice(0, 10), bg: 'bg-slate-100', fg: 'text-slate-700' };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${style.bg} ${style.fg}`}>
      {style.label}
    </span>
  );
};

const B2BTabLeads: React.FC<Props> = ({ apiCall }) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [tier, setTier] = useState<string>('');
  const [minJobs, setMinJobs] = useState<number | ''>('');
  const [atsType, setAtsType] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [hasAtsFilter, setHasAtsFilter] = useState<string>(''); // '' | 'yes' | 'no'
  const [countryFilter, setCountryFilter] = useState<string>('');
  const [listStatus, setListStatus] = useState<string | null>(null);
  const [listOffset, setListOffset] = useState(0);
  const [hasMoreCompanies, setHasMoreCompanies] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Selection
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Bulk-add modal
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDomains, setBulkDomains] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState<string | null>(null);

  const listFilters = useMemo(() => ({
    search: debouncedSearch || undefined,
    tier: tier || undefined,
    min_open_jobs: minJobs === '' ? undefined : Number(minJobs),
    ats_type: atsType || undefined,
    source: sourceFilter || undefined,
    country: countryFilter || undefined,
    has_ats: hasAtsFilter === 'yes' ? true : hasAtsFilter === 'no' ? false : undefined,
  }), [debouncedSearch, tier, minJobs, atsType, sourceFilter, countryFilter, hasAtsFilter]);

  const load = useCallback(async (append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setListOffset(0);
    }
    setError(null);
    setListStatus(append ? 'Loading more companies…' : 'Fetching companies from database…');
    try {
      const offset = append ? listOffset : 0;
      const data = await listCompanies(apiCall, {
        ...listFilters,
        limit: LIST_PAGE_SIZE,
        offset,
      });
      setCompanies((prev) => (append ? [...prev, ...data] : data));
      const nextOffset = offset + data.length;
      setListOffset(nextOffset);
      setHasMoreCompanies(data.length === LIST_PAGE_SIZE);
      setListStatus(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load companies');
      setListStatus(null);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [apiCall, listFilters, listOffset]);

  useEffect(() => { load(false); }, [listFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMoreCompanies = () => {
    if (!loadingMore && hasMoreCompanies) load(true);
  };

  const submitBulk = async () => {
    const domains = bulkDomains
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (domains.length === 0) return;
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const r = await bulkAddCompanies(apiCall, domains);
      setBulkResult(`Added ${r.added}, skipped dupes ${r.skipped_duplicates}, invalid ${r.skipped_invalid}`);
      await load();
    } catch (e: any) {
      setBulkResult(`Error: ${e?.message || 'failed'}`);
    } finally {
      setBulkBusy(false);
    }
  };

  const uploadCsv = async (file: File) => {
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const r = await uploadCompaniesCSV(apiCall, file) as any;
      setBulkResult(`Added ${r.added}, skipped dupes ${r.skipped_duplicates}, invalid ${r.skipped_invalid}`);
      await load();
    } catch (e: any) {
      setBulkResult(`Error: ${e?.message || 'failed'}`);
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Discovery panel (BA scraping + full-chain trigger) */}
      <B2BDiscoveryPanel apiCall={apiCall} onCompaniesChanged={() => load(false)} />
      <B2BWorkersPanel apiCall={apiCall} onCompaniesChanged={() => load(false)} />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      {/* LEFT */}
      <div className="lg:col-span-3 bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="p-3 border-b border-slate-100 flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-1 mr-1">
            <Building2 className="w-4 h-4 text-slate-500" />
            <HeadingWithInfo
              info="Alle importierten Firmen. Filter nach Tier, ATS, Quelle, Land. Briefcase = offene Jobs, Mail = Contacts."
              className="text-xs font-medium text-slate-600"
            >
              Companies
            </HeadingWithInfo>
          </div>
          <div className="flex-1 min-w-[180px] relative">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search domain / name"
              className="w-full pl-8 pr-2 py-1.5 text-sm border border-slate-200 rounded-md"
            />
          </div>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            className="text-sm border border-slate-200 rounded-md px-2 py-1.5"
          >
            <option value="">All tiers</option>
            {Object.entries(TIER_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <select
            value={atsType}
            onChange={(e) => setAtsType(e.target.value)}
            className="text-sm border border-slate-200 rounded-md px-2 py-1.5"
          >
            <option value="">All ATS</option>
            {['greenhouse','lever','personio','ashby','workable','smartrecruiters','recruitee','softgarden','teamtailor','needle','join','workday','onlyfy','rexx','dvinci'].map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="text-sm border border-slate-200 rounded-md px-2 py-1.5"
            title="Filter by source (where the company came from)"
          >
            <option value="">All sources</option>
            <option value="bundesagentur">From BA</option>
            <option value="ats_slug">From ATS</option>
            <option value="csv_upload">From CSV</option>
            <option value="manual_bulk">From bulk</option>
            <option value="manual">Manual</option>
          </select>
          <select
            value={hasAtsFilter}
            onChange={(e) => setHasAtsFilter(e.target.value)}
            className="text-sm border border-slate-200 rounded-md px-2 py-1.5"
            title="Filter by ATS detection status"
          >
            <option value="">ATS status</option>
            <option value="yes">Has ATS</option>
            <option value="no">No ATS yet</option>
          </select>
          <select
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            className="text-sm border border-slate-200 rounded-md px-2 py-1.5"
            title="Filter by HQ country"
          >
            <option value="">All countries</option>
            {['DE', 'AT', 'CH', 'SE', 'NL', 'UK', 'US'].map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <input
            type="number"
            min={0}
            value={minJobs}
            onChange={(e) => setMinJobs(e.target.value === '' ? '' : Number(e.target.value))}
            placeholder="Min open jobs"
            className="w-24 text-sm border border-slate-200 rounded-md px-2 py-1.5"
          />
          <button
            onClick={() => load()}
            className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-50"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4 text-slate-600" />
          </button>
          <button
            onClick={() => setBulkOpen(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-pink-600 text-white rounded-md hover:bg-pink-700"
          >
            <Plus className="w-4 h-4" /> Add
          </button>
          <InfoTip
            align="right"
            text="Domains manuell einfügen oder CSV hochladen. Gleiche Domain wird übersprungen (kein Duplikat)."
          />
        </div>

        {!loading && companies.length > 0 && (
          <div className="px-3 py-1 text-[11px] text-slate-400 border-b border-slate-50">
            Showing {companies.length} companies
            {(sourceFilter || hasAtsFilter || countryFilter || debouncedSearch) && ' (filtered)'}
          </div>
        )}

        {loading ? (
          <div className="p-4">
            <LoadingNudge
              title="Loading companies…"
              step={listStatus || 'Applying filters and fetching from database'}
            />
          </div>
        ) : error ? (
          <ErrorBox message={error} onRetry={() => load(false)} />
        ) : companies.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            No companies match. Add a few via the <strong>+</strong> button to get started.
          </div>
        ) : (
          <>
          <ul className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
            {companies.map((c) => (
              <li
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                className={`px-3 py-2 cursor-pointer hover:bg-slate-50 ${
                  selectedId === c.id ? 'bg-pink-50' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800 truncate flex items-center gap-2">
                      <span className="truncate">{c.name || c.domain}</span>
                      {c.source && <SourceBadge source={c.source} />}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {c.domain}
                      {c.hq_country ? ` · ${c.hq_country}` : ''}
                      {c.ats_type ? ` · ${c.ats_type}` : ' · no ATS yet'}
                      {` · ${TIER_LABELS[c.tier] || c.tier}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="inline-flex items-center gap-1 text-emerald-700">
                      <Briefcase className="w-3 h-3" /> {c.open_jobs_count}
                    </span>
                    <span className="inline-flex items-center gap-1 text-blue-700">
                      <Mail className="w-3 h-3" /> {c.contacts_count}
                    </span>
                    <span className="text-slate-400">
                      <ChevronRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <div className="px-3 py-2 border-t border-slate-100 flex items-center justify-between gap-2 bg-slate-50">
            <span className="text-xs text-slate-500">
              {companies.length} loaded{hasMoreCompanies ? ' · more available' : ''}
            </span>
            {hasMoreCompanies && (
              <button
                type="button"
                onClick={loadMoreCompanies}
                disabled={loadingMore}
                className="text-xs px-2.5 py-1 rounded border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-50"
              >
                {loadingMore ? 'Loading…' : `Load ${LIST_PAGE_SIZE} more`}
              </button>
            )}
          </div>
          </>
        )}
      </div>

      {/* RIGHT */}
      <div className="lg:col-span-2">
        {selectedId == null ? (
          <div className="bg-white rounded-lg border border-slate-200 p-6 text-sm text-slate-400 text-center">
            Select a company to see jobs + contacts
          </div>
        ) : (
          <CompanyDetailPanel
            apiCall={apiCall}
            companyId={selectedId}
            onChanged={() => load(false)}
          />
        )}
      </div>

      {/* Bulk add modal */}
      {bulkOpen && (
        <BulkAddModal
          onClose={() => { setBulkOpen(false); setBulkResult(null); }}
          domains={bulkDomains}
          setDomains={setBulkDomains}
          submit={submitBulk}
          uploadCsv={uploadCsv}
          busy={bulkBusy}
          result={bulkResult}
        />
      )}
      </div>
    </div>
  );
};

export default B2BTabLeads;

// ============================================================
// Sub-components
// ============================================================

const DETECT_STEPS = [
  'Loading company record…',
  'Probing /careers, /karriere, /jobs on company domain…',
  'Scanning page HTML + redirects for ATS embeds…',
  'Trying public ATS APIs (Personio, Greenhouse, Lever…) via domain slug…',
  'Saving ats_type + ats_slug to database…',
  'Fetching open jobs from ATS public API…',
];

const CompanyDetailPanel: React.FC<{
  apiCall: ApiCall;
  companyId: number;
  onChanged: () => void;
}> = ({ apiCall, companyId, onChanged }) => {
  const [company, setCompany] = useState<Company | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [detectResult, setDetectResult] = useState<{
    ok: boolean;
    message: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingTier, setSavingTier] = useState(false);

  const detectStep = useStepProgress(detecting, DETECT_STEPS, 2800);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, j, ct] = await Promise.all([
        getCompany(apiCall, companyId),
        listJobsByCompany(apiCall, companyId, true),
        listContacts(apiCall, { company_id: companyId, limit: 200 }),
      ]);
      setCompany(c);
      setJobs(j);
      setContacts(ct);
    } catch (e: any) {
      setError(e?.message || 'Failed to load detail');
    } finally {
      setLoading(false);
    }
  }, [apiCall, companyId]);

  // Reset detect-result whenever we switch to a different company.
  useEffect(() => { setDetectResult(null); load(); }, [load]);

  const onDetect = async () => {
    setDetecting(true);
    setDetectResult(null);
    try {
      const r = (await detectAts(apiCall, companyId)) as any;
      if (r?.ats_type) {
        const jobsPart =
          typeof r.jobs_fetched === 'number'
            ? ` · ${r.jobs_fetched} open job${r.jobs_fetched === 1 ? '' : 's'} fetched`
            : '';
        setDetectResult({
          ok: true,
          message: `Detected ${r.ats_type}${r.ats_slug ? ` (slug: ${r.ats_slug})` : ''} · confidence ${(r.confidence ?? 0).toFixed(2)}${jobsPart}`,
        });
      } else {
        setDetectResult({
          ok: false,
          message:
            'No ATS detected. ' +
            (r?.notes || 'Try opening the careers page manually and pasting the URL into careers_url, or skip this lead.'),
        });
      }
      await load();
      onChanged();
    } catch (e: any) {
      setDetectResult({ ok: false, message: e?.message || 'Detection failed' });
    } finally {
      setDetecting(false);
    }
  };

  const onTierChange = async (newTier: string) => {
    if (!company || newTier === company.tier) return;
    setSavingTier(true);
    try {
      const updated = await updateCompany(apiCall, companyId, { tier: newTier });
      setCompany(updated);
      onChanged();
    } catch (e: any) {
      setError(e?.message || 'Failed to update tier');
    } finally {
      setSavingTier(false);
    }
  };

  if (loading) return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <LoadingNudge title="Loading company detail…" step="Fetching jobs, contacts, and ATS metadata" />
    </div>
  );
  if (error)   return <ErrorBox message={error} onRetry={load} />;
  if (!company) return <div className="bg-white rounded-lg border border-slate-200 p-6 text-sm text-slate-400">Company not found</div>;

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-4 max-h-[80vh] overflow-y-auto">
      <div>
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{company.name || company.domain}</h3>
            <div className="text-xs text-slate-500">{company.domain}</div>
          </div>
          <button onClick={load} className="p-1 text-slate-400 hover:text-slate-600">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
          <div className="border border-slate-200 rounded px-2 py-1">
            <div className="text-[10px] text-slate-400 uppercase flex items-center gap-0.5">
              Tier
              <InfoTip
                align="left"
                text="hot/warm/cold aus Job-Signalen (ICP). customer/lost/blocked manuell - ICP überschreibt diese nicht."
              />
            </div>
            <select
              value={company.tier}
              onChange={(e) => onTierChange(e.target.value)}
              disabled={savingTier}
              className="mt-0.5 w-full text-xs border-0 p-0 bg-transparent text-slate-800 focus:ring-0 cursor-pointer disabled:opacity-50"
            >
              {Object.entries(TIER_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <Pill label="ATS" value={company.ats_type || '-'} info="Erkanntes Bewerber-System (Personio, Ashby, …)." />
          <Pill label="Country" value={company.hq_country || '-'} info="Geschätztes HQ-Land - nicht immer korrekt bei .com-Domains." />
          <Pill label="Industry" value={company.industry || '-'} info="Branche falls bekannt (oft leer bei BA-Import)." />
        </div>
        {company.careers_url && (
          <a href={company.careers_url} target="_blank" rel="noopener noreferrer"
             className="mt-2 text-xs text-pink-600 inline-flex items-center gap-1 hover:underline">
            Careers <ExternalLink className="w-3 h-3" />
          </a>
        )}
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <ActionWithInfo info="Probiert Karriere-URLs, erkennt ATS und holt sofort offene Stellen für diese eine Firma.">
              <button
                onClick={onDetect}
                disabled={detecting}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-slate-800 text-white rounded-md hover:bg-slate-700 disabled:opacity-50"
              >
                {detecting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                {detecting ? 'Detecting…' : (company.ats_type ? 'Re-detect ATS' : 'Detect ATS')}
              </button>
            </ActionWithInfo>
            {detectResult && !detecting && (
              <span
                className={`inline-flex items-center px-2 py-1 rounded text-[11px] ${
                  detectResult.ok
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-amber-100 text-amber-900'
                }`}
              >
                {detectResult.message}
              </span>
            )}
          </div>
          {detecting && detectStep && (
            <LoadingNudge
              title={`Detecting ATS for ${company.domain}`}
              step={detectStep}
              steps={DETECT_STEPS}
            />
          )}
        </div>
      </div>

      <section>
        <div className="flex items-center gap-2 mb-2">
          <Briefcase className="w-4 h-4 text-emerald-600" />
          <HeadingWithInfo
            className="font-medium text-slate-800 text-sm"
            info="Aktive Stellen aus dem ATS - klassifiziert nach Pipeline › Classify + ICP."
          >
            Open jobs ({jobs.length})
          </HeadingWithInfo>
        </div>
        {jobs.length === 0 ? (
          <div className="text-xs text-slate-400">
            No active jobs yet. Click <b>Detect ATS</b> (detects + fetches) or run
            {' '}<b>Discover jobs</b> in Pipeline workers.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {jobs.slice(0, 30).map((j) => (
              <li key={j.id} className="text-xs border border-slate-100 rounded p-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium text-slate-800">{j.title || '(no title)'}</div>
                  {j.apply_url && (
                    <a href={j.apply_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-slate-700">
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                <div className="text-slate-500 mt-0.5">
                  {[j.category, j.seniority, j.location, j.remote_status].filter(Boolean).join(' · ')}
                </div>
                {j.tech_stack && j.tech_stack.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {j.tech_stack.slice(0, 6).map((t, i) => (
                      <span key={i} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px]">{t}</span>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <DecisionMakerPanel
        apiCall={apiCall}
        companyId={companyId}
        domain={company.domain}
        onContactAdded={() => { void load(); onChanged(); }}
      />

      <section>
        <div className="flex items-center gap-2 mb-2">
          <Mail className="w-4 h-4 text-blue-600" />
          <HeadingWithInfo
            className="font-medium text-slate-800 text-sm"
            info="Emails aus Impressum/Team (Pipeline › Find contacts) oder manuell/auto aus Decision-maker-Suche."
          >
            Contacts ({contacts.length})
          </HeadingWithInfo>
        </div>
        {contacts.length === 0 ? (
          <div className="text-xs text-slate-400">No contacts yet. Run <code className="font-mono text-[11px]">discover_contacts</code>.</div>
        ) : (
          <ul className="space-y-1">
            {contacts.map((c) => (
              <li key={c.id} className={`text-xs border rounded p-2 ${
                c.do_not_contact ? 'border-red-200 bg-red-50' : 'border-slate-100'
              }`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800 truncate">{c.email}</div>
                    {(c.first_name || c.last_name) && (
                      <div className="text-slate-500 truncate">
                        {[c.first_name, c.last_name].filter(Boolean).join(' ')}
                        {c.title && ` · ${c.title}`}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[10px]">
                    {c.email_verified ? (
                      <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">verified</span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">unverified</span>
                    )}
                    {c.do_not_contact && (
                      <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700">DNC</span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

const Pill: React.FC<{ label: string; value: string; info?: string }> = ({ label, value, info }) => (
  <div className="border border-slate-200 rounded px-2 py-1">
    <div className="text-[10px] text-slate-400 uppercase flex items-center gap-0.5">
      {label}
      {info && <InfoTip text={info} align="left" />}
    </div>
    <div className="text-slate-800 truncate">{value}</div>
  </div>
);

// =============================================================================
// Decision-maker discovery panel (CTO / Head of Engineering / Recruiter)
// =============================================================================

const TITLE_OPTIONS = [
  'CTO',
  'Head of Engineering',
  'VP Engineering',
  'Engineering Manager',
  'Tech Lead',
  'Head of Talent Acquisition',
  'Senior Tech Recruiter',
  'Founder',
  'CEO',
];

const SOURCE_META: Record<string, { label: string; bg: string; fg: string; icon: React.ComponentType<{ className?: string }> }> = {
  ddg_linkedin: { label: 'Web search', bg: 'bg-sky-100', fg: 'text-sky-700', icon: Linkedin },
  web_search: { label: 'Web search', bg: 'bg-sky-100', fg: 'text-sky-700', icon: Linkedin },
  team_page: { label: 'Team page', bg: 'bg-violet-100', fg: 'text-violet-700', icon: Globe },
  impressum: { label: 'Impressum', bg: 'bg-amber-100', fg: 'text-amber-800', icon: FileText },
};

function sourceMeta(source: string) {
  const primary = source.split('+')[0] || source;
  return SOURCE_META[primary] || { label: primary, bg: 'bg-slate-100', fg: 'text-slate-700', icon: Globe };
}

const DiscoveryDiagnosticsBar: React.FC<{ discovery: DecisionMakerDiscovery }> = ({ discovery }) => {
  const d = discovery.diagnostics;
  if (!d) return null;
  const ms = discovery.timings_ms?.total_ms ?? 0;

  const badge = (label: string, ok: boolean, info: string, err: string | null) => (
    <span
      title={err || info}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] whitespace-nowrap ${
        err
          ? 'bg-red-100 text-red-700'
          : ok
          ? 'bg-emerald-100 text-emerald-700'
          : 'bg-slate-100 text-slate-500'
      }`}
    >
      {label}: {err ? 'err' : info}
    </span>
  );

  return (
    <div className="mb-2 flex flex-wrap items-center gap-1">
      {badge(
        'Web',
        d.ddg.hits > 0,
        d.ddg.attempted ? `${d.ddg.hits} hits` : 'skipped',
        d.ddg.error,
      )}
      {badge(
        'Team',
        d.team_page.hits > 0,
        `${d.team_page.pages_found} page${d.team_page.pages_found === 1 ? '' : 's'} → ${d.team_page.hits}`,
        d.team_page.error,
      )}
      {badge(
        'Impressum',
        d.impressum.hits > 0,
        d.impressum.found ? `${d.impressum.hits} found` : 'no page',
        d.impressum.error,
      )}
      <span className="text-[10px] text-slate-400 ml-auto">{ms} ms</span>
    </div>
  );
};

const DecisionMakerPanel: React.FC<{
  apiCall: ApiCall;
  companyId: number;
  domain: string;
  onContactAdded: () => void;
}> = ({ apiCall, companyId, domain, onContactAdded }) => {
  const [searches, setSearches] = useState<DecisionMakerSearch | null>(null);
  const [searchesError, setSearchesError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [title, setTitle] = useState('CTO');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [emailOverride, setEmailOverride] = useState('');

  const [pattern, setPattern] = useState<EmailPattern | null>(null);
  const [patternLoading, setPatternLoading] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<{ ok: boolean; message: string } | null>(null);

  const [discovering, setDiscovering] = useState(false);
  const [discovery, setDiscovery] = useState<DecisionMakerDiscovery | null>(null);
  const [discoveryError, setDiscoveryError] = useState<string | null>(null);
  const [addingCandidate, setAddingCandidate] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getDecisionMakerSearch(apiCall, companyId);
        if (!cancelled) setSearches(r);
      } catch (e: any) {
        if (!cancelled) setSearchesError(e?.message || 'Failed to load searches');
      }
    })();
    return () => { cancelled = true; };
  }, [apiCall, companyId]);

  const runAutoDiscover = useCallback(async () => {
    setDiscovering(true);
    setDiscoveryError(null);
    setDiscovery(null);
    try {
      const r = await discoverDecisionMakers(apiCall, companyId);
      setDiscovery(r);
    } catch (e: any) {
      setDiscoveryError(e?.message || 'Discovery failed');
    } finally {
      setDiscovering(false);
    }
  }, [apiCall, companyId]);

  const addCandidate = useCallback(async (c: DecisionMakerCandidate, idx: number) => {
    if (!c.suggested_email && !c.first_name) return;
    setAddingCandidate(idx);
    try {
      const email = c.suggested_email
        || (c.first_name && c.last_name ? `${c.first_name}.${c.last_name}@${domain}`.toLowerCase().replace(/[^a-z0-9@\.\-]/g, '') : null);
      if (!email) throw new Error('Cannot derive email - open form and fill manually.');
      await createContact(apiCall, {
        company_id: companyId,
        email,
        first_name: c.first_name || undefined,
        last_name: c.last_name || undefined,
        title: c.title || undefined,
        linkedin_url: c.linkedin_url || undefined,
        source: `auto_${c.source}`,
        email_verified: false,
        email_confidence: c.confidence,
      } as any);
      // Optimistically remove from list
      setDiscovery((prev) => prev ? {
        ...prev,
        candidates: prev.candidates.filter((_, i) => i !== idx),
      } : prev);
      onContactAdded();
    } catch (e: any) {
      setDiscoveryError(e?.message || 'Failed to add candidate');
    } finally {
      setAddingCandidate(null);
    }
  }, [apiCall, companyId, domain, onContactAdded]);

  const prefillFromCandidate = useCallback((c: DecisionMakerCandidate) => {
    setFirstName(c.first_name || '');
    setLastName(c.last_name || '');
    setTitle(c.title || 'CTO');
    setLinkedinUrl(c.linkedin_url || '');
    setEmailOverride(c.suggested_email || '');
    setShowForm(true);
  }, []);

  // Auto-fetch the email pattern + guess whenever first+last are filled.
  const debFirst = useDebouncedValue(firstName, 350);
  const debLast = useDebouncedValue(lastName, 350);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!debFirst.trim() || !debLast.trim()) {
        setPattern(null);
        return;
      }
      setPatternLoading(true);
      try {
        const r = await getEmailPattern(apiCall, companyId, debFirst, debLast);
        if (!cancelled) setPattern(r);
      } catch {
        if (!cancelled) setPattern(null);
      } finally {
        if (!cancelled) setPatternLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiCall, companyId, debFirst, debLast]);

  const effectiveEmail = emailOverride.trim() || pattern?.guess || '';

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setTitle('CTO');
    setLinkedinUrl('');
    setEmailOverride('');
    setPattern(null);
    setSaveResult(null);
  };

  const submit = async () => {
    setSaving(true);
    setSaveResult(null);
    try {
      if (!effectiveEmail || !effectiveEmail.includes('@')) {
        throw new Error('Email missing - fill first+last name or paste an email override.');
      }
      await createContact(apiCall, {
        company_id: companyId,
        email: effectiveEmail.toLowerCase().trim(),
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        title: title.trim() || undefined,
        linkedin_url: linkedinUrl.trim() || undefined,
        source: 'manual_linkedin',
        email_verified: false,
        email_confidence: pattern?.confidence ?? 0.5,
      } as any);
      setSaveResult({ ok: true, message: `Added ${effectiveEmail} as ${title}` });
      resetForm();
      onContactAdded();
    } catch (e: any) {
      setSaveResult({ ok: false, message: e?.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="border border-indigo-100 bg-indigo-50/30 rounded p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Linkedin className="w-4 h-4 text-indigo-600" />
          <HeadingWithInfo
            className="font-medium text-slate-800 text-sm"
            info="CTO/Founder/Recruiter finden - Auto-search (Team, Impressum, Web) oder manuelle LinkedIn-Links."
          >
            Find decision maker
          </HeadingWithInfo>
        </div>
        <div className="flex items-center gap-1.5">
          <ActionWithInfo info="Team-Seite, Impressum, JSON-LD und Web-Suche - ~15 s, kostenlos. Top-Kandidaten mit 1-Klick als Contact.">
            <button
              onClick={runAutoDiscover}
              disabled={discovering}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
            >
              {discovering ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
              {discovering ? 'Searching…' : 'Auto-search'}
            </button>
          </ActionWithInfo>
          <ActionWithInfo info="Name, Rolle und LinkedIn manuell eintragen - Email wird aus Firmen-Pattern geraten.">
            <button
              onClick={() => setShowForm((v) => !v)}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-white border border-indigo-200 text-indigo-700 rounded hover:bg-indigo-50"
            >
              <UserPlus className="w-3 h-3" />
              {showForm ? 'Hide form' : 'Manual'}
            </button>
          </ActionWithInfo>
        </div>
      </div>

      <p className="text-[11px] text-slate-500 mb-2">
        <b>Auto-search</b> scans team/about pages (incl. JSON-LD), Impressum, and
        web search for LinkedIn profiles - free, ~15 s. Manual role buttons still
        work if auto-search finds nothing
        {pattern?.pattern ? ` (email pattern: ${pattern.pattern})` : ''}.
      </p>

      {discoveryError && (
        <div className="text-[11px] text-red-700 bg-red-50 rounded p-1.5 mb-2">{discoveryError}</div>
      )}

      {discovery && (
        <DiscoveryDiagnosticsBar discovery={discovery} />
      )}

      {discovery && discovery.candidates.length === 0 && (
        <div className="text-[11px] text-slate-500 bg-white border border-slate-200 rounded p-2 mb-2">
          No candidates auto-found ({discovery.timings_ms?.total_ms ?? 0} ms).
          Try the manual LinkedIn buttons below.
        </div>
      )}

      {discovery && discovery.candidates.length > 0 && (
        <div className="mb-3 space-y-1.5">
          <div className="text-[10px] text-slate-500 uppercase">
            {discovery.candidates.length} candidate{discovery.candidates.length === 1 ? '' : 's'} found
            {discovery.timings_ms && ` · ${discovery.timings_ms.total_ms} ms`}
          </div>
          {discovery.candidates.map((c, idx) => {
            const sm = sourceMeta(c.source);
            const SourceIcon = sm.icon;
            return (
              <div key={idx} className="border border-slate-200 bg-white rounded p-2 text-xs">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800 truncate">{c.name}</div>
                    <div className="text-slate-500 text-[11px] truncate">
                      {c.title || 'no title'}
                      {c.linkedin_url && (
                        <a
                          href={c.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-1.5 inline-flex items-center text-sky-700 hover:underline"
                        >
                          <ExternalLink className="w-2.5 h-2.5 inline" />
                        </a>
                      )}
                    </div>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] whitespace-nowrap ${sm.bg} ${sm.fg}`}>
                    <SourceIcon className="w-2.5 h-2.5" />
                    {sm.label}
                  </span>
                </div>
                {c.suggested_email && (
                  <div className="text-[11px] text-slate-600 font-mono truncate">
                    {c.suggested_email}
                    <span className="ml-1 text-slate-400">
                      ({Math.round(c.confidence * 100)}%)
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 mt-1.5">
                  <button
                    onClick={() => addCandidate(c, idx)}
                    disabled={addingCandidate === idx || (!c.suggested_email && !c.first_name)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {addingCandidate === idx ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                    Add
                  </button>
                  <button
                    onClick={() => prefillFromCandidate(c)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] bg-white border border-slate-200 text-slate-700 rounded hover:bg-slate-50"
                  >
                    Edit first
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="text-[10px] text-slate-500 uppercase mt-2 mb-1">Manual LinkedIn search</div>
      {searchesError && (
        <div className="text-[11px] text-red-700 bg-red-50 rounded p-1.5 mb-2">{searchesError}</div>
      )}

      {searches && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {searches.queries.map((q) => (
            <a
              key={q.role_key}
              href={q.search_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-white border border-indigo-200 text-indigo-700 rounded hover:bg-indigo-50"
            >
              <ExternalLink className="w-3 h-3" />
              {q.label}
            </a>
          ))}
        </div>
      )}

      {showForm && (
        <div className="bg-white border border-indigo-100 rounded p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-slate-500 uppercase">First name</label>
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Jane"
                className="w-full text-xs border border-slate-200 rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase">Last name</label>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Müller"
                className="w-full text-xs border border-slate-200 rounded px-2 py-1"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] text-slate-500 uppercase">Title</label>
            <select
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full text-xs border border-slate-200 rounded px-2 py-1"
            >
              {TITLE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div>
            <label className="text-[10px] text-slate-500 uppercase">LinkedIn URL (optional)</label>
            <input
              value={linkedinUrl}
              onChange={(e) => setLinkedinUrl(e.target.value)}
              placeholder="https://linkedin.com/in/..."
              className="w-full text-xs border border-slate-200 rounded px-2 py-1 font-mono"
            />
          </div>

          <div>
            <label className="text-[10px] text-slate-500 uppercase flex items-center justify-between">
              <span>Email{pattern && (
                <span className="ml-1 normal-case text-slate-400">
                  (pattern conf. {Math.round((pattern.confidence ?? 0) * 100)}%
                  {pattern.examples_used > 0 ? `, ${pattern.examples_used} ex.` : ', no impressum data'})
                </span>
              )}</span>
              {patternLoading && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
            </label>
            <input
              value={emailOverride || pattern?.guess || ''}
              onChange={(e) => setEmailOverride(e.target.value)}
              placeholder={pattern?.guess || `firstname.lastname@${domain}`}
              className={`w-full text-xs border rounded px-2 py-1 font-mono ${
                emailOverride
                  ? 'border-slate-300'
                  : pattern?.guess
                    ? 'border-emerald-200 bg-emerald-50/50'
                    : 'border-slate-200'
              }`}
            />
            {pattern && !emailOverride && pattern.guess && (
              <div className="text-[10px] text-slate-400 mt-0.5">
                Auto-suggested - edit if wrong.
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={submit}
              disabled={saving || (!emailOverride && !pattern?.guess) || !firstName.trim()}
              className="flex-1 px-3 py-1.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center justify-center gap-1"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
              {saving ? 'Saving…' : 'Add contact'}
            </button>
            <button
              onClick={resetForm}
              className="px-3 py-1.5 text-xs bg-slate-100 text-slate-600 rounded hover:bg-slate-200"
            >
              Clear
            </button>
          </div>

          {saveResult && (
            <div className={`text-[11px] rounded p-1.5 ${
              saveResult.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
            }`}>{saveResult.message}</div>
          )}
        </div>
      )}
    </section>
  );
};

interface BulkProps {
  onClose: () => void;
  domains: string;
  setDomains: (s: string) => void;
  submit: () => void;
  uploadCsv: (f: File) => void;
  busy: boolean;
  result: string | null;
}

const BulkAddModal: React.FC<BulkProps> = ({ onClose, domains, setDomains, submit, uploadCsv, busy, result }) => (
  <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-lg max-w-lg w-full overflow-hidden">
      <div className="flex items-center justify-between p-3 border-b border-slate-100">
        <HeadingWithInfo info="Domains oder CSV - eine Domain pro Zeile. Existierende Domains werden übersprungen.">
          Add companies
        </HeadingWithInfo>
        <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-4 space-y-3">
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Domains (one per line)</label>
          <textarea
            value={domains}
            onChange={(e) => setDomains(e.target.value)}
            rows={8}
            placeholder={"sennder.com\ngorillas.io\nrasa.com"}
            className="w-full text-sm border border-slate-200 rounded p-2 font-mono"
          />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={submit} disabled={busy || !domains.trim()}
                  className="flex-1 px-3 py-2 bg-pink-600 text-white rounded text-sm hover:bg-pink-700 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Add via list'}
          </button>
          <label className="flex-1 px-3 py-2 bg-slate-100 rounded text-sm text-slate-700 cursor-pointer flex items-center justify-center gap-1 hover:bg-slate-200">
            <Upload className="w-4 h-4" />
            CSV
            <input type="file" accept=".csv,.txt" className="hidden"
                   onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadCsv(f); }} />
          </label>
        </div>
        {result && (
          <div className={`text-sm rounded p-2 ${
            result.startsWith('Error') ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'
          }`}>{result}</div>
        )}
      </div>
    </div>
  </div>
);
